import { defineStore } from 'pinia'
import apiClient from '@/api/client'

export interface AdminUser {
  admin_id: number
  name: string
  nickname: string
  access_rights: Record<string, boolean>
}

interface AuthState {
  user: AdminUser | null
  csrfToken: string | null
  initializing: boolean
  initData: Omit<InitResponse, 'csrfToken' | 'user'> | null
  /** Set after password OK but TOTP code not yet entered. NOT persisted. */
  totpRequired: boolean
  /** Set when server returns password_change_required: true. NOT persisted. */
  passwordChangeRequired: boolean
}

export interface InitResponse {
  csrfToken: string
  user: AdminUser | null
  users_count: number
  new_users_count: number
  active_count: number
  paid_users_count: number
  reports_count: number
  games_count: number
  admins_count: number
  web_users_day: number
  web_users_week: number
  web_users_month: number
  web_users_total: number
  /** Number of days used as the inactivity threshold for the feed filter. */
  search_inactive_days: number
  /** Users visible in the feed: last_seen within search_inactive_days. */
  search_visible_users: number
  /** Users hidden from the feed: last_seen older than search_inactive_days or NULL. */
  search_hidden_users: number
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    csrfToken: null,
    initializing: true,
    initData: null,
    totpRequired: false,
    passwordChangeRequired: false,
  }),

  getters: {
    isAuthenticated: (s): boolean => s.user !== null,
    hasRight: (s) => (right: string): boolean =>
      s.user?.access_rights[right] === true,
    initials: (s): string =>
      s.user?.nickname?.[0]?.toUpperCase() ?? '?',
  },

  actions: {
    async init(): Promise<void> {
      this.initializing = true
      try {
        const data = await apiClient.get<never, InitResponse>('/api/init')
        this.csrfToken = data.csrfToken ?? null
        this.user = data.user ?? null
        this.initData = {
          users_count: data.users_count ?? 0,
          new_users_count: data.new_users_count ?? 0,
          active_count: data.active_count ?? 0,
          paid_users_count: data.paid_users_count ?? 0,
          reports_count: data.reports_count ?? 0,
          games_count: data.games_count ?? 0,
          admins_count: data.admins_count ?? 0,
          web_users_day: data.web_users_day ?? 0,
          web_users_week: data.web_users_week ?? 0,
          web_users_month: data.web_users_month ?? 0,
          web_users_total: data.web_users_total ?? 0,
          search_inactive_days: data.search_inactive_days ?? 30,
          search_visible_users: data.search_visible_users ?? 0,
          search_hidden_users: data.search_hidden_users ?? 0,
        }
      } catch {
        // Network failure or server error — stay unauthenticated, guards will redirect
        this.user = null
        this.csrfToken = null
      } finally {
        this.initializing = false
      }
    },

    setUser(user: AdminUser | null): void {
      this.user = user
    },

    setCsrfToken(token: string): void {
      this.csrfToken = token
    },

    logout(): void {
      this.user = null
      this.csrfToken = null
      this.totpRequired = false
      this.passwordChangeRequired = false
    },

    setTotpRequired(val: boolean): void {
      this.totpRequired = val
    },

    setPasswordChangeRequired(val: boolean): void {
      this.passwordChangeRequired = val
    },
  },
})
